import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Camera, CheckCircle, ChevronDown, ImagePlus, Layers, Loader2, Plus,
  RefreshCw, Trash2, User, Users, X, ZoomIn,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AvatarCreatorForm, type AvatarDraft } from './AvatarCreatorForm';
import { AVATAR_SCENE_PRESETS } from './avatarScenePresets';
import { ImageLightbox } from './ImageLightbox';

// Zdjęcia z zakładki "Zdjęcia referencyjne" produktu (produkt/opakowanie itd.)
export interface ProductReferenceImage {
  id: string;
  kind: string;
  title: string;
}

// KREATOR AVATARÓW jako krok łańcucha zasobów wideo: tu tworzysz postaci
// (portret generuje ChatGPT) i ich sceny (inne otoczenie, produkt w ręce).
// Gotowe ujęcia przypisujesz potem do scen w Skrypcie produkcyjnym (edytor
// ujęć) - modal produkcji nie wybiera już avatara.

export interface WorkshopAvatar {
  id: string;
  name: string;
  isSystem?: boolean;
  status: string;
  error: string | null;
  previewUrl: string | null;
  heygenAvatarId: string | null;
  prompt: string | null;
  scenes: Array<{
    id: string;
    name: string;
    status: string;
    error: string | null;
    previewUrl: string | null;
    heygenLookId: string | null;
  }>;
}

const POLL_INTERVAL_MS = 8000;

function statusBadge(status: string, error?: string | null) {
  if (status === 'PENDING' || status === 'GENERATING') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-medium text-warning">
        <Loader2 className="w-3 h-3 animate-spin" /> {status === 'PENDING' ? 'W kolejce...' : 'Generowanie...'}
      </span>
    );
  }
  if (status === 'FAILED') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-medium text-red-400" title={error || undefined}>
        <AlertCircle className="w-3 h-3" /> Błąd
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[10px] font-medium text-success">
      <CheckCircle className="w-3 h-3" /> Gotowy
    </span>
  );
}

interface AvatarWorkshopPanelProps {
  productId: string;
}

export function AvatarWorkshopPanel({ productId }: AvatarWorkshopPanelProps) {
  const [avatars, setAvatars] = useState<WorkshopAvatar[]>([]);
  const [referenceImages, setReferenceImages] = useState<ProductReferenceImage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [sceneModalAvatar, setSceneModalAvatar] = useState<WorkshopAvatar | null>(null);
  const [seriesModalAvatar, setSeriesModalAvatar] = useState<WorkshopAvatar | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [regenerateTarget, setRegenerateTarget] = useState<{ kind: 'avatar' | 'scene'; id: string; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'avatar' | 'scene'; id: string; name: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAvatars = useCallback(async () => {
    try {
      const res = await labApi.getAvatars();
      setAvatars(res.data.data || []);
    } catch (err) {
      console.error('Failed to load avatars:', err);
    }
  }, []);

  useEffect(() => {
    void loadAvatars().finally(() => setLoading(false));
  }, [loadAvatars]);

  useEffect(() => {
    let cancelled = false;
    labApi.getReferenceImages(productId).then(res => {
      if (!cancelled) setReferenceImages((res.data.data || []).map((image: any) => ({ id: image.id, kind: image.kind, title: image.title })));
    }).catch(err => console.error('Failed to load reference images:', err));
    return () => { cancelled = true; };
  }, [productId]);

  const hasPending = avatars.some(avatar =>
    ['PENDING', 'GENERATING'].includes(avatar.status)
    || avatar.scenes.some(scene => ['PENDING', 'GENERATING'].includes(scene.status)),
  );

  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void loadAvatars(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, loadAvatars]);

  const readyLooks = avatars.reduce((count, avatar) => {
    const base = avatar.status === 'READY' && avatar.previewUrl ? 1 : 0;
    return count + base + avatar.scenes.filter(scene => scene.status === 'READY' && scene.previewUrl).length;
  }, 0);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.kind === 'avatar') await labApi.deleteAvatar(deleteTarget.id);
      else await labApi.deleteAvatarScene(deleteTarget.id);
      setDeleteTarget(null);
      await loadAvatars();
    } catch {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="bg-surface-hover/30 rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-surface-hover/40 transition-colors"
      >
        <Users className="w-4 h-4 text-accent-primary shrink-0" />
        <span className="text-xs font-semibold text-text-primary">Kreator avatarów</span>
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-raised border border-border-subtle text-text-secondary">
          {readyLooks}
        </span>
        {hasPending && <Loader2 className="w-3 h-3 animate-spin text-warning shrink-0" />}
        <ChevronDown className={`w-3.5 h-3.5 text-text-muted ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-3 border-t border-border-subtle pt-3">
          <p className="text-[10px] text-text-muted">
            Postaci i ich sceny (portrety generuje ChatGPT). Gotowe ujęcia przypisujesz do scen w Skrypcie produkcyjnym.
          </p>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Stwórz avatara
          </button>

          {loading ? (
            <div className="flex items-center gap-2 py-3 text-xs text-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie avatarów...
            </div>
          ) : avatars.length === 0 ? (
            <p className="text-[11px] text-text-disabled">Brak avatarów - stwórz pierwszego, żeby przypisywać ujęcia do scen skryptu.</p>
          ) : (
            <div className="space-y-3">
              {avatars.map(avatar => (
                <div key={avatar.id} className="rounded-lg border border-border-subtle bg-surface-raised/60 p-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-surface-hover border border-border-subtle shrink-0 relative group/thumb">
                      {avatar.previewUrl ? (
                        <>
                          <img src={avatar.previewUrl} alt={avatar.name} loading="lazy" className="w-full h-full object-cover" />
                          <button
                            onClick={() => setLightbox({ src: avatar.previewUrl!, alt: avatar.name })}
                            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                            title="Podgląd"
                            aria-label="Podgląd grafiki avatara"
                          >
                            <ZoomIn className="w-4 h-4 text-white" />
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {['PENDING', 'GENERATING'].includes(avatar.status)
                            ? <Loader2 className="w-4 h-4 animate-spin text-warning" />
                            : <User className="w-4 h-4 text-text-disabled" />}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-text-primary truncate">{avatar.name}</span>
                        {avatar.isSystem && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-accent-info/15 text-accent-info border border-accent-info/20 shrink-0">
                            Katalog
                          </span>
                        )}
                        {statusBadge(avatar.status, avatar.error)}
                      </div>
                      {avatar.status === 'FAILED' && avatar.error && (
                        <span className="text-[10px] text-red-400 block truncate">{avatar.error}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {avatar.status === 'READY' && (
                        <>
                          <button
                            onClick={() => setSceneModalAvatar(avatar)}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-accent-info bg-info/10 border border-accent-info/20 rounded-md hover:bg-info/15 transition-all"
                          >
                            <Camera className="w-3 h-3" /> Scena
                          </button>
                          <button
                            onClick={() => setSeriesModalAvatar(avatar)}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-md hover:bg-accent-primary/15 transition-all"
                          >
                            <Layers className="w-3 h-3" /> Seria
                          </button>
                        </>
                      )}
                      {['READY', 'FAILED'].includes(avatar.status) && !avatar.isSystem && (
                        <button
                          onClick={() => setRegenerateTarget({ kind: 'avatar', id: avatar.id, name: avatar.name })}
                          disabled={avatar.scenes.length > 0 && avatar.status === 'READY'}
                          title={avatar.scenes.length > 0 && avatar.status === 'READY' ? 'Usuń sceny przed regeneracją postaci' : 'Regeneruj'}
                          className="p-1 text-text-muted hover:text-accent-primary transition-colors disabled:opacity-30"
                          aria-label="Regeneruj avatara"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!avatar.isSystem && (
                        <button
                          onClick={() => setDeleteTarget({ kind: 'avatar', id: avatar.id, name: avatar.name })}
                          className="p-1 text-text-muted hover:text-red-400 transition-colors"
                          title="Usuń avatara"
                          aria-label="Usuń avatara"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {avatar.scenes.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2.5">
                      {avatar.scenes.map(scene => (
                        <div key={scene.id} className="rounded-md border border-border-subtle overflow-hidden bg-surface-hover/30 group relative">
                          <div className="aspect-square bg-surface-hover">
                            {scene.previewUrl ? (
                              <img src={scene.previewUrl} alt={scene.name} loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {['PENDING', 'GENERATING'].includes(scene.status)
                                  ? <Loader2 className="w-4 h-4 animate-spin text-warning" />
                                  : <Camera className="w-4 h-4 text-text-disabled" />}
                              </div>
                            )}
                          </div>
                          <div className="px-1.5 py-1">
                            <span className="text-[9px] font-medium text-text-secondary block truncate">{scene.name}</span>
                            {statusBadge(scene.status, scene.error)}
                          </div>
                          <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {scene.previewUrl && (
                              <button
                                onClick={() => setLightbox({ src: scene.previewUrl!, alt: scene.name })}
                                className="p-0.5 rounded bg-black/60 text-text-secondary hover:text-white transition-colors"
                                title="Podgląd"
                                aria-label="Podgląd grafiki sceny"
                              >
                                <ZoomIn className="w-3 h-3" />
                              </button>
                            )}
                            {['READY', 'FAILED'].includes(scene.status) && (
                              <button
                                onClick={() => setRegenerateTarget({ kind: 'scene', id: scene.id, name: scene.name })}
                                className="p-0.5 rounded bg-black/60 text-text-secondary hover:text-white transition-colors"
                                title="Regeneruj scenę"
                                aria-label="Regeneruj scenę"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteTarget({ kind: 'scene', id: scene.id, name: scene.name })}
                              className="p-0.5 rounded bg-black/60 text-text-secondary hover:text-red-400 transition-colors"
                              title="Usuń scenę"
                              aria-label="Usuń scenę"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <WorkshopCreateAvatarModal
          onCreated={async () => {
            setCreateOpen(false);
            await loadAvatars();
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {sceneModalAvatar && (
        <WorkshopCreateSceneModal
          avatar={sceneModalAvatar}
          productId={productId}
          referenceImages={referenceImages}
          onCreated={async () => {
            setSceneModalAvatar(null);
            await loadAvatars();
          }}
          onClose={() => setSceneModalAvatar(null)}
        />
      )}

      {seriesModalAvatar && (
        <AvatarSeriesModal
          avatar={seriesModalAvatar}
          referenceImages={referenceImages}
          onCreated={async () => {
            setSeriesModalAvatar(null);
            await loadAvatars();
          }}
          onClose={() => setSeriesModalAvatar(null)}
        />
      )}

      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}

      {regenerateTarget && (
        <WorkshopRegenerateModal
          target={regenerateTarget}
          onDone={async () => {
            setRegenerateTarget(null);
            await loadAvatars();
          }}
          onClose={() => setRegenerateTarget(null)}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title={deleteTarget?.kind === 'avatar' ? 'Usunąć avatara?' : 'Usunąć scenę?'}
        message={deleteTarget ? <>Czy na pewno chcesz usunąć <strong>{deleteTarget.name}</strong>?{deleteTarget.kind === 'avatar' ? ' Usunięte zostaną też wszystkie sceny tego avatara.' : ''}</> : ''}
        confirmLabel="Usuń"
        danger
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ============================================
// Modale kreatora (kompaktowe, wewnątrz łańcucha zasobów)
// ============================================

function ModalShell({ title, icon: Icon, children, onClose, wide }: {
  title: string;
  icon: typeof Users;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-surface-raised rounded-xl border border-border w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Icon className="w-4 h-4 text-accent-primary" /> {title}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function WorkshopCreateAvatarModal({ onCreated, onClose }: { onCreated: () => Promise<void>; onClose: () => void }) {
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleCreate = async (draft: AvatarDraft) => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.createAvatar(draft);
      await onCreated();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się utworzyć avatara.');
      setStarting(false);
    }
  };

  return (
    <ModalShell title="Nowy avatar" icon={Users} onClose={onClose} wide>
      <p className="text-xs text-text-muted mb-4">
        Portret generuje ChatGPT (1-2 min, w tle). Reguły naturalności UGC dokładamy automatycznie.
      </p>
      <AvatarCreatorForm idPrefix="workshop" submitting={starting} submitLabel="Generuj avatara" error={modalError} onSubmit={handleCreate} />
    </ModalShell>
  );
}

function WorkshopCreateSceneModal({ avatar, productId, referenceImages, onCreated, onClose }: {
  avatar: WorkshopAvatar;
  productId: string;
  referenceImages: ProductReferenceImage[];
  onCreated: () => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const toggleRef = (id: string) => {
    setSelectedRefIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 2) next.add(id);
      return next;
    });
  };

  const handleUpload = async (file: File) => {
    setModalError(null);
    setUploading(true);
    try {
      const res = await labApi.uploadAvatarReference(file);
      setReferenceImageUrl(res.data.data?.url || null);
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się wgrać zdjęcia.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.createAvatarScene(avatar.id, {
        name: name.trim(),
        prompt: prompt.trim(),
        referenceImageUrl,
        referenceImageIds: Array.from(selectedRefIds),
      });
      await onCreated();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się utworzyć sceny.');
      setStarting(false);
    }
  };

  return (
    <ModalShell title={`Nowa scena: ${avatar.name}`} icon={Camera} onClose={onClose}>
      <p className="text-xs text-text-muted mb-4">
        Ta sama postać w nowym ujęciu - opisz otoczenie, strój lub kadr. Możesz dodać zdjęcie produktu (avatar z produktem w ręce).
      </p>
      <div className="space-y-4">
        <div>
          <label htmlFor="workshop-scene-name" className="text-xs font-medium text-text-secondary mb-1.5 block">Nazwa sceny</label>
          <input
            id="workshop-scene-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder='np. "Kuchnia z produktem"'
            className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
          />
        </div>
        <div>
          <label htmlFor="workshop-scene-prompt" className="text-xs font-medium text-text-secondary mb-1.5 block">
            Opis sceny (po angielsku dla najlepszych rezultatów)
          </label>
          <textarea
            id="workshop-scene-prompt"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="np. standing in a bright kitchen, holding the product in her right hand, showing it to the camera"
            className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
          />
        </div>
        <div>
          <span className="text-xs font-medium text-text-secondary mb-1.5 block">Zdjęcia referencyjne produktu (opcjonalnie, max 2)</span>
          {referenceImages.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 mb-2">
              {referenceImages.map(image => {
                const isSelected = selectedRefIds.has(image.id);
                return (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => toggleRef(image.id)}
                    title={image.title}
                    className={`shrink-0 w-16 rounded-lg border overflow-hidden text-center transition-all ${
                      isSelected ? 'border-accent-primary ring-1 ring-accent-primary/40' : 'border-border-subtle hover:border-accent-primary/40'
                    }`}
                  >
                    <span className="block aspect-square bg-surface-hover">
                      <img src={labApi.getReferenceImageFileUrl(productId, image.id)} alt={image.title} loading="lazy" className="w-full h-full object-cover" />
                    </span>
                    <span className={`block text-[9px] px-1 py-1 truncate ${isSelected ? 'text-accent-primary font-medium' : 'text-text-secondary'}`}>
                      {image.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) void handleUpload(file);
              e.target.value = '';
            }}
          />
          {referenceImageUrl ? (
            <div className="flex items-center gap-3">
              <img src={referenceImageUrl} alt="Zdjęcie referencyjne" className="w-14 h-14 rounded-lg object-cover border border-border-subtle" />
              <button onClick={() => setReferenceImageUrl(null)} className="text-[11px] text-text-muted hover:text-red-400 transition-colors">
                Usuń zdjęcie
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary bg-surface-hover border border-border rounded-lg hover:border-accent-primary/30 hover:text-text-primary transition-all disabled:opacity-40"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              Wgraj własną grafikę
            </button>
          )}
        </div>
      </div>

      {modalError && (
        <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
        </div>
      )}

      <div className="flex items-center justify-between mt-5">
        <button
          onClick={handleCreate}
          disabled={starting || uploading || !name.trim() || prompt.trim().length < 10}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
        >
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          Generuj scenę
        </button>
        <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
          Anuluj
        </button>
      </div>
    </ModalShell>
  );
}

function WorkshopRegenerateModal({ target, onDone, onClose }: {
  target: { kind: 'avatar' | 'scene'; id: string; name: string };
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const [instruction, setInstruction] = useState('');
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleRegenerate = async () => {
    setModalError(null);
    setStarting(true);
    try {
      if (target.kind === 'avatar') await labApi.regenerateAvatar(target.id, instruction.trim() || undefined);
      else await labApi.regenerateAvatarScene(target.id, instruction.trim() || undefined);
      await onDone();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się uruchomić regeneracji.');
      setStarting(false);
    }
  };

  return (
    <ModalShell title={`Regeneruj: ${target.name}`} icon={RefreshCw} onClose={onClose}>
      <label htmlFor="workshop-regenerate-instruction" className="text-xs font-medium text-text-secondary mb-1.5 block">
        Co poprawić? (opcjonalnie)
      </label>
      <textarea
        id="workshop-regenerate-instruction"
        value={instruction}
        onChange={e => setInstruction(e.target.value)}
        rows={2}
        maxLength={500}
        placeholder="np. younger face, warmer smile, less makeup..."
        className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
      />
      {modalError && (
        <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
        </div>
      )}
      <div className="flex items-center justify-between mt-5">
        <button
          onClick={handleRegenerate}
          disabled={starting}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
        >
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Regeneruj
        </button>
        <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
          Anuluj
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================
// "Seria": paczka predefiniowanych ujęć avatara (max 20). Klik pozycji
// rozwija gotowy prompt (edytowalny) + checkboxy Produkt / Opakowanie
// (zdjęcia z zakładki "Zdjęcia referencyjne" jako referencje generacji).
// ============================================

interface SeriesEntry {
  checked: boolean;
  prompt: string;
  product: boolean;
  packaging: boolean;
}

function AvatarSeriesModal({ avatar, referenceImages, onCreated, onClose }: {
  avatar: WorkshopAvatar;
  referenceImages: ProductReferenceImage[];
  onCreated: () => Promise<void>;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<Record<string, SeriesEntry>>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const productRef = referenceImages.find(image => image.kind === 'PRODUCT');
  const packagingRef = referenceImages.find(image => image.kind === 'PACKAGING');

  const toggleEntry = (key: string, prompt: string) => {
    setEntries(prev => {
      const current = prev[key];
      if (current?.checked) {
        setExpandedKey(expanded => (expanded === key ? null : expanded));
        return { ...prev, [key]: { ...current, checked: false } };
      }
      setExpandedKey(key);
      return { ...prev, [key]: current ? { ...current, checked: true } : { checked: true, prompt, product: false, packaging: false } };
    });
  };

  const updateEntry = (key: string, patch: Partial<SeriesEntry>) => {
    setEntries(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const selectedCount = Object.values(entries).filter(entry => entry.checked).length;

  const handleGenerate = async () => {
    setModalError(null);
    setStarting(true);
    try {
      const scenes = AVATAR_SCENE_PRESETS
        .filter(preset => entries[preset.key]?.checked)
        .map(preset => {
          const entry = entries[preset.key];
          const referenceImageIds = [
            ...(entry.product && productRef ? [productRef.id] : []),
            ...(entry.packaging && packagingRef ? [packagingRef.id] : []),
          ];
          return { name: preset.label, prompt: entry.prompt.trim(), referenceImageIds };
        });
      await labApi.createAvatarScenesBatch(avatar.id, scenes);
      await onCreated();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się uruchomić serii.');
      setStarting(false);
    }
  };

  return (
    <ModalShell title={`Seria ujęć: ${avatar.name}`} icon={Layers} onClose={onClose} wide>
      <p className="text-xs text-text-muted mb-4">
        Zaznacz ujęcia do wygenerowania - każde ma gotowy prompt, który możesz dopisać/zmienić po rozwinięciu. Checkbox Produkt/Opakowanie dodaje zdjęcie z zakładki "Zdjęcia referencyjne" jako referencję.
      </p>

      <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1">
        {AVATAR_SCENE_PRESETS.map(preset => {
          const entry = entries[preset.key];
          const isChecked = Boolean(entry?.checked);
          const isExpanded = expandedKey === preset.key && isChecked;
          return (
            <div key={preset.key} className={`rounded-lg border transition-all ${isChecked ? 'border-accent-primary/30 bg-accent-primary/5' : 'border-border bg-surface-hover/30'}`}>
              <div className="flex items-center gap-2.5 px-3 py-2">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleEntry(preset.key, preset.prompt)}
                  className="accent-current shrink-0"
                />
                <button
                  type="button"
                  onClick={() => (isChecked ? setExpandedKey(isExpanded ? null : preset.key) : toggleEntry(preset.key, preset.prompt))}
                  className="flex-1 min-w-0 text-left"
                >
                  <span className={`text-xs font-medium ${isChecked ? 'text-accent-primary' : 'text-text-primary'}`}>{preset.label}</span>
                </button>
                {isChecked && (
                  <ChevronDown className={`w-3.5 h-3.5 text-text-muted shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                )}
              </div>
              {isExpanded && entry && (
                <div className="px-3 pb-3 space-y-2 border-t border-border-subtle pt-2">
                  <textarea
                    value={entry.prompt}
                    onChange={e => updateEntry(preset.key, { prompt: e.target.value })}
                    rows={2}
                    maxLength={1000}
                    className="w-full text-[11px] bg-surface-hover border border-border rounded-lg px-2.5 py-1.5 text-text-primary focus:outline-none focus:border-accent-primary/40"
                  />
                  <div className="flex items-center gap-4">
                    <label className={`flex items-center gap-1.5 text-[11px] ${productRef ? 'text-text-secondary cursor-pointer' : 'text-text-disabled cursor-not-allowed'}`} title={productRef ? undefined : 'Brak zdjęcia typu Produkt w Zdjęciach referencyjnych'}>
                      <input
                        type="checkbox"
                        checked={entry.product}
                        disabled={!productRef}
                        onChange={e => updateEntry(preset.key, { product: e.target.checked })}
                        className="accent-current"
                      />
                      Produkt
                    </label>
                    <label className={`flex items-center gap-1.5 text-[11px] ${packagingRef ? 'text-text-secondary cursor-pointer' : 'text-text-disabled cursor-not-allowed'}`} title={packagingRef ? undefined : 'Brak zdjęcia typu Opakowanie w Zdjęciach referencyjnych'}>
                      <input
                        type="checkbox"
                        checked={entry.packaging}
                        disabled={!packagingRef}
                        onChange={e => updateEntry(preset.key, { packaging: e.target.checked })}
                        className="accent-current"
                      />
                      Opakowanie
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalError && (
        <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
        </div>
      )}

      <div className="flex items-center justify-between mt-5">
        <button
          onClick={handleGenerate}
          disabled={starting || selectedCount === 0}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
        >
          {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
          Generuj serię ({selectedCount})
        </button>
        <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
          Anuluj
        </button>
      </div>
    </ModalShell>
  );
}

